import { Transition } from '@headlessui/react';
import dayjs from 'dayjs';
import { Fragment } from 'react';
import {
	BsCalendar2Check as Calendar2Check,
	BsCalendar2XFill as Calendar2XFill,
	BsCardText as CardText,
	BsChatLeftDots as ChatLeftDots,
	BsPencilSquare as PencilSquare,
	BsX as X,
} from 'react-icons/bs';

interface ParsedDetails {
	firstname?: string;
	lastname?: string;
	emailaddress?: string;
	displaytext?: string;
	displayname?: string;
	mobilephone?: string;
	assignedtopublicid?: string;
	coordinatorgroupid?: string;
	itemtype?: string;
	required?: string;
	actionid?: string;
	contactid?: string;
	from?: string;
	did?: string;
	conversationid?: string;
}

interface Notification {
	id: number | string;
	type: string;
	datetime: string;
	publicdid: string;
	title: string;
	details: string;
	notificationid: string;
	instanceDate?: string;
}

interface NotificationItemProps {
	notification: Notification;
	snoozeFunc: (id: number | string, title: string, notificationid: string) => void;
	onCloseFunc: () => void;
	handleNotificationClick: (
		notificationid: string,
		isLead?: boolean,
		accept?: boolean,
		publicdid?: string,
		instanceDate?: string,
	) => void;
}

const NotificationItem = ({
	notification,
	snoozeFunc,
	onCloseFunc,
	handleNotificationClick,
}: NotificationItemProps) => {
	// eslint-disable-next-line no-useless-escape
	const objKeysRegex =
		/({|,)(?:\s*)(?:')?([A-Za-z_$\.][A-Za-z0-9_ \-\.$]*)(?:')?(?:\s*):/g;

	const {
		id,
		type,
		datetime,
		publicdid,
		title,
		details,
		notificationid,
		instanceDate,
	} = notification;

	let parsedDetails: ParsedDetails = {};

	const sanitizeJson = (input: string): string => {
		if (!input || typeof input !== 'string') return '{}';

		try {
			const entries = input
				?.replace(/^{|}$/g, '')
				?.split(',')
				?.map((pair) => pair.split(':').map((str) => str.trim()));
			const cleanedEntries = entries?.map(([key, value]) => {
				const cleanKey = `"${key?.replace(/"/g, '')}"`;
				const cleanValue =
					isNaN(Number(value)) && value !== 'true' && value !== 'false'
						? `"${value?.replace(/"/g, '')}"`
						: value;
				return `${cleanKey}:${cleanValue}`;
			});

			return `{${cleanedEntries.join(',')}}`;
		} catch (e) {
			return JSON.stringify({
				firstname: '',
				lastname: '',
				emailaddress: '',
				displaytext: '',
				displayname: '',
				mobilephone: '',
				assignedtopublicid: '',
				coordinatorgroupid: '',
				itemtype: '',
				required: '',
				actionid: '',
				contactid: '',
				from: '',
				did: '',
				conversationid: '',
			});
		}
	};

	try {
		parsedDetails = !type.includes('calendar')
			? JSON.parse(details)
			: JSON.parse(details.replace(objKeysRegex, '$1"$2":'));
	} catch (e) {
		try {
			const sanitizedInput = !type.includes('calendar')
				? sanitizeJson(details)
				: sanitizeJson(details.replace(objKeysRegex, '$1"$2":'));
			parsedDetails = JSON.parse(sanitizedInput);
		} catch (sanitizeError) {
			parsedDetails = {
				firstname: '',
				lastname: '',
				emailaddress: '',
				displaytext: '',
				displayname: '',
				mobilephone: '',
				assignedtopublicid: '',
				coordinatorgroupid: '',
				itemtype: '',
				required: '',
				actionid: '',
				contactid: '',
				from: '',
				did: '',
				conversationid: '',
			};
		}
	}

	const {
		firstname = '',
		lastname = '',
		emailaddress = '',
		displaytext = '',
		displayname = '',
		mobilephone = '',
		assignedtopublicid = '',
		coordinatorgroupid = '',
		itemtype = '',
		required = '',
		actionid = '',
		contactid = '',
		from = '',
		did = '',
		conversationid = '',
	} = parsedDetails;

	return (
		<Transition
			appear
			as={Fragment}
			leave="transition-all ease-out"
			leaveFrom="opacity-1"
			leaveTo="opacity-0 translate-y-3.5rem"
			show
		>
			<div
				className="iq-notifications-item"
				data-id={id}
				data-notification-type={type}
				data-notificationid={notificationid}
				id={`iq_notification_${id}`}
			>
				{type.includes('calendar') || type.includes('lead') ? (
					<div
						className="notify-left cursor-pointer"
						onClick={() =>
							handleNotificationClick(
								notificationid,
								type.includes('lead'),
								false,
								publicdid,
								instanceDate,
							)
						}
					>
						<span className="media-object">
							{!type.includes('lead') ? (
								<>
									{' '}
									<span>{dayjs(datetime).format('D')}</span>
									<span>{dayjs(datetime).format('MMM')}</span>
								</>
							) : (
								<span className="fs-5">
									<CardText />
								</span>
							)}
						</span>
					</div>
				) : (
					<div className="notify-left">
						<span className="media-object">
							{!type.includes('textmessages') ? (
								<>
									{' '}
									<span>{dayjs(datetime).format('D')}</span>
									<span>{dayjs(datetime).format('MMM')}</span>
								</>
							) : (
								<span className="fs-5">
									<ChatLeftDots />
								</span>
							)}
						</span>
					</div>
				)}
				<div className="notify-close">
					<span
						className="close-icon cursor-pointer"
						title="Snooze"
						onClick={() => snoozeFunc(id, title, notificationid)}
					>
						<X size={28} />
					</span>
				</div>
				{type === 'textmessages' ? (
					<div
						className="notify-body cursor-pointer"
						onClick={() => {
							/* eslint-disable-next-line no-undef, @typescript-eslint/no-explicit-any */
							(window as any).$.iqTextMessage_Conversation_Popup(
								conversationid,
								did,
								from,
								displayname,
								contactid,
							);
							onCloseFunc();
						}}
					>
						<h5>{displayname}</h5>
						<div className="text-muted">{displaytext}</div>
					</div>
				) : (
					<div className="notify-body">
						{type.includes('calendar') || type.includes('lead') ? (
							<h5
								className="cursor-pointer"
								onClick={() =>
									handleNotificationClick(
										notificationid,
										type.includes('lead'),
										false,
										publicdid,
										instanceDate,
									)
								}
							>
								{title}
							</h5>
						) : (
							<h5>{title}</h5>
						)}
						<div className="displaytext">{displaytext}</div>
						<div className="name">
							{firstname}&nbsp;{lastname}
						</div>
						<div className="phone">{mobilephone}</div>
						<div className="email">{emailaddress}</div>
					</div>
				)}
				{type.includes('message') ||
					(type.includes('todos') && (
						<div className="notify-buttons">
							{required === 'F' && (
								<span
									className="remove-icon cursor-pointer"
									title="Remove"
									onClick={() => {
										/* eslint-disable-next-line no-undef, @typescript-eslint/no-explicit-any */
										(window as any).$.iqActions_DoIgnore(
											'Contact',
											actionid,
											contactid,
											'',
											'',
											'',
											'',
										);
										onCloseFunc();
									}}
								>
									<Calendar2XFill color="var(--bs-red)" size={26} />
								</span>
							)}
							{type !== 'textmessages' && (
								<span
									className="action-icon cursor-pointer"
									title="Take Action"
									onClick={() => {
										/* eslint-disable-next-line no-undef, @typescript-eslint/no-explicit-any */
										(window as any).$.iqActions_DoComplete(
											'Contact',
											actionid,
											itemtype,
											contactid,
											'',
											'',
											'',
											'',
										);
										onCloseFunc();
									}}
								>
									<PencilSquare color="var(--bs-green" size={26} />
								</span>
							)}
							{assignedtopublicid && coordinatorgroupid && (
								<span
									className="action-icon cursor-pointer"
									title="Accept"
									onClick={() => {
										/* eslint-disable-next-line no-undef, @typescript-eslint/no-explicit-any */
										(window as any).$.iqActions_DoAccept(
											'Contact',
											actionid,
											contactid,
											'',
											'',
											'',
											'',
										);
										onCloseFunc();
									}}
								>
									<Calendar2Check color="green" size={26} />
								</span>
							)}
						</div>
					))}
				{type.includes('lead') && (
					<div className="notify-buttons">
						<span
							className="action-icon cursor-pointer"
							onClick={() =>
								handleNotificationClick(
									notificationid,
									true,
									true,
									publicdid,
									instanceDate,
								)
							}
						>
							<Calendar2Check color="green" size={26} />
						</span>
						<span
							className="action-icon cursor-pointer"
							onClick={() =>
								handleNotificationClick(
									notificationid,
									true,
									false,
									publicdid,
									instanceDate,
								)
							}
						>
							<Calendar2XFill color="var(--bs-red)" size={26} />
						</span>
					</div>
				)}
				{datetime && (type.includes('message') || type.includes('calendar')) ? (
					<div className="notify-timestamp">
						{type.includes('calendar') && type.includes('todo') ? (
							<p
								className="text-muted cursor-pointer"
								onClick={() => handleNotificationClick(notificationid)}
							>
								{dayjs(datetime).format(
									!type.includes('calendar')
										? '[On] MM/YY/DD [at] h:mm A'
										: 'h:mm A',
								)}
							</p>
						) : (
							<p className="text-muted">
								{dayjs(datetime).format(
									!type.includes('calendar')
										? '[On] MM/YY/DD [at] h:mm A'
										: 'h:mm A',
								)}
							</p>
						)}
					</div>
				) : null}
			</div>
		</Transition>
	);
};

export default NotificationItem;
